import { useState, useEffect } from 'react';

const API = '/api';

const Icon = ({ d, size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    mail: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
    lock: ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z', 'M7 11V7a5 5 0 0 1 10 0v4'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    eyeOff: ['M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94', 'M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19', 'M1 1l22 22'],
    building: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    arrow: 'M5 12h14M12 5l7 7-7 7',
};

export default function ClientLogin({ onLogin }) {
    const [form, setForm] = useState({ email: '', password: '' });
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [branding, setBranding] = useState(null);
    const [brandingLoading, setBrandingLoading] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '');
        const opId = params.get('op');
        if (opId) {
            fetch(`${API}/operators/${opId}`)
                .then(res => res.json())
                .then(data => {
                    if (data && data.companyName) setBranding(data);
                })
                .catch(err => console.error("Error fetching branding:", err));
        }
    }, []);

    const handleEmailBlur = async () => {
        if (!form.email || !form.email.includes('@') || branding) return;
        setBrandingLoading(true);
        try {
            const res = await fetch(`${API}/client-branding?email=${encodeURIComponent(form.email)}`);
            if (res.ok) {
                const data = await res.json();
                if (data && data.companyName) setBranding(data);
            }
        } catch (err) {
            // silently fail — branding is cosmetic
        } finally {
            setBrandingLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/client-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: form.email, password: form.password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Login failed');
            localStorage.setItem('ops-user', JSON.stringify(data));
            onLogin(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.bgBlob1} />
            <div style={styles.bgBlob2} />

            <div style={styles.card}>
                {/* Brand header */}
                <div style={styles.brandHeader}>
                    <div style={styles.logoRing}>
                        <div style={styles.logoInner}>
                            {branding?.companyLogo ? (
                                <img
                                    src={`${API}/uploads/${branding.companyLogo}`}
                                    alt={branding.companyName}
                                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain' }}
                                />
                            ) : (
                                <Icon d={icons.building} size={28} color="white" />
                            )}
                        </div>
                    </div>
                    <div style={styles.brandBadge}>CLIENT PORTAL</div>
                    {branding?.companyName ? (
                        <h1 style={styles.brandName}>
                            {branding.companyName}
                        </h1>
                    ) : brandingLoading ? (
                        <h1 style={{ ...styles.brandName, color: '#94a3b8', fontSize: 18 }}>…</h1>
                    ) : null}
                    <p style={styles.brandSub}>Sign in to your client account</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} style={styles.form}>
                    {error && (
                        <div style={styles.errorBox}>
                            <span style={{ fontSize: 16 }}>⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Client Email</label>
                        <div style={styles.inputWrap}>
                            <span style={styles.inputIcon}>
                                <Icon d={icons.mail} size={17} color="#6366f1" />
                            </span>
                            <input
                                id="client-email"
                                style={styles.input}
                                type="email"
                                placeholder="your@company.com"
                                required
                                value={form.email}
                                onChange={e => setForm({ ...form, email: e.target.value })}
                                onBlur={handleEmailBlur}
                                autoComplete="email"
                            />
                        </div>
                    </div>

                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>Password</label>
                        <div style={styles.inputWrap}>
                            <span style={styles.inputIcon}>
                                <Icon d={icons.lock} size={17} color="#6366f1" />
                            </span>
                            <input
                                id="client-password"
                                style={{ ...styles.input, paddingRight: 48 }}
                                type={showPass ? 'text' : 'password'}
                                placeholder="Enter your password"
                                required
                                value={form.password}
                                onChange={e => setForm({ ...form, password: e.target.value })}
                                autoComplete="current-password"
                            />
                            <button type="button" onClick={() => setShowPass(s => !s)} style={styles.eyeBtn}>
                                <Icon d={showPass ? icons.eyeOff : icons.eye} size={16} color="#94a3b8" />
                            </button>
                        </div>
                    </div>

                    <button
                        id="client-login-btn"
                        type="submit"
                        disabled={loading}
                        style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
                    >
                        {loading ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                <span style={styles.spinner} /> Signing in…
                            </span>
                        ) : (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                Sign In <Icon d={icons.arrow} size={18} color="white" />
                            </span>
                        )}
                    </button>
                </form>

                {/* Footer */}
                <div style={styles.footer}>
                    <span style={styles.footerText}>Are you an operator?</span>
                    <a
                        href="#operator-login"
                        style={styles.footerLink}
                        onClick={e => { e.preventDefault(); window.location.hash = 'operator-login'; window.location.reload(); }}
                    >
                        Operator Login →
                    </a>
                </div>
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 40%, #ede9fe 100%)',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        position: 'relative', overflow: 'hidden', padding: 20,
    },
    bgBlob1: {
        position: 'absolute', top: -120, right: -120, width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    bgBlob2: {
        position: 'absolute', bottom: -100, left: -100, width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    card: {
        background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)',
        borderRadius: 24, padding: '48px 44px 36px',
        boxShadow: '0 20px 64px rgba(99,102,241,0.13), 0 4px 20px rgba(0,0,0,0.08)',
        width: '100%', maxWidth: 420, position: 'relative', zIndex: 1,
        border: '1px solid rgba(99,102,241,0.15)',
    },
    brandHeader: { textAlign: 'center', marginBottom: 36 },
    logoRing: {
        width: 80, height: 80, borderRadius: '50%',
        background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(99,102,241,0.28)',
    },
    logoInner: {
        width: 56, height: 56, borderRadius: '50%',
        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    brandBadge: {
        display: 'inline-block', padding: '3px 12px', borderRadius: 20,
        background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
        color: '#3730a3', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
        marginBottom: 10, border: '1px solid #a5b4fc',
    },
    brandName: {
        fontSize: 28, fontWeight: 900, color: '#1e1b4b',
        letterSpacing: '-0.03em', margin: '0 0 6px',
    },
    brandSub: { fontSize: 14, color: '#6b7280', fontWeight: 500, margin: 0 },
    form: { display: 'flex', flexDirection: 'column', gap: 20 },
    errorBox: {
        background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
        color: '#dc2626', fontSize: 13, fontWeight: 600,
    },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 7 },
    label: { fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: '0.04em', textTransform: 'uppercase' },
    inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    inputIcon: { position: 'absolute', left: 14, display: 'flex', alignItems: 'center', pointerEvents: 'none' },
    input: {
        width: '100%', padding: '13px 16px 13px 44px',
        border: '1.5px solid #e5e7eb', borderRadius: 12,
        fontSize: 14, fontFamily: 'inherit', color: '#1f2937',
        background: '#f9fafb', outline: 'none', boxSizing: 'border-box',
        transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    eyeBtn: {
        position: 'absolute', right: 12, background: 'none', border: 'none',
        cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4,
    },
    submitBtn: {
        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
        color: 'white', border: 'none', borderRadius: 12, padding: '14px 24px',
        fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
        boxShadow: '0 4px 16px rgba(99,102,241,0.38)', marginTop: 4,
        fontFamily: 'inherit',
    },
    spinner: {
        width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)',
        borderTop: '2px solid white', borderRadius: '50%',
        display: 'inline-block', animation: 'spin 0.8s linear infinite',
    },
    footer: {
        marginTop: 28, textAlign: 'center', display: 'flex',
        alignItems: 'center', justifyContent: 'center', gap: 8,
    },
    footerText: { fontSize: 13, color: '#9ca3af' },
    footerLink: { fontSize: 13, fontWeight: 700, color: '#4f46e5', textDecoration: 'none' },
};
